import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

const useStyles = makeStyles({
  dialogContent: {
    minWidth: '500px',
    maxHeight: '600px',
    overflow: 'auto',
  },
  versionItem: {
    borderBottom: '1px solid #e0e0e0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    margin: '0 4px',
  },
  previousStatus: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  currentStatus: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: '#999',
  },
});

const ProjectVersionHistory = ({ orgId, projectId, onStatusReverted }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reverting, setReverting] = useState(null);

  useEffect(() => {
    if (open) {
      fetchVersions();
    }
  }, [open, orgId, projectId]);

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api_super_admin/orgs/${orgId}/projects/${projectId}/versions`);
      const data = await response.json();
      
      if (data.success) {
        setVersions(data.versions);
      } else {
        setError(data.error || 'Failed to fetch versions');
      }
    } catch (err) {
      setError('Error fetching version history: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (versionId, previousStatus) => {
    if (!window.confirm(`Are you sure you want to revert the project status to "${previousStatus}"?`)) {
      return;
    }

    setReverting(versionId);
    setError(null);
    
    try {
      const response = await fetch(`/api_super_admin/orgs/${orgId}/projects/${projectId}/revert_to_version`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ version_id: versionId }),
      });

      const data = await response.json();

      if (data.success) {
        setOpen(false);
        if (onStatusReverted) {
          onStatusReverted();
        }
        // Optionally show a success message
        alert(`Project status successfully reverted to "${previousStatus}"`);
      } else {
        setError(data.error || 'Failed to revert status');
      }
    } catch (err) {
      setError('Error reverting status: ' + err.message);
    } finally {
      setReverting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<HistoryIcon />}
        onClick={() => setOpen(true)}
        size="small"
      >
        View Status History
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Project Status History</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} style={{ marginBottom: '1rem' }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <CircularProgress />
            </div>
          ) : versions.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">No status history available</Typography>
            </div>
          ) : (
            <List>
              {versions.map((version) => (
                <ListItem key={version.id} className={classes.versionItem}>
                  <ListItemText
                    primary={
                      <div>
                        <span className={`${classes.statusBadge} ${classes.previousStatus}`}>
                          {formatStatus(version.previous_status)}
                        </span>
                        <span style={{ margin: '0 8px' }}>→</span>
                        <span className={`${classes.statusBadge} ${classes.currentStatus}`}>
                          {formatStatus(version.current_status)}
                        </span>
                      </div>
                    }
                    secondary={
                      <div style={{ marginTop: '8px' }}>
                        <Typography variant="caption" display="block">
                          {formatDate(version.created_at)}
                        </Typography>
                        {version.whodunnit && (
                          <Typography variant="caption" color="textSecondary">
                            Changed by: {version.whodunnit}
                          </Typography>
                        )}
                      </div>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="revert"
                      onClick={() => handleRevert(version.id, version.previous_status)}
                      disabled={reverting === version.id}
                      color="primary"
                    >
                      {reverting === version.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <RestoreIcon />
                      )}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectVersionHistory;

